/**
 * 起動時の配線。環境変数 → 認証セッション → urql クライアント。
 *
 * 接続先はすべてビルド時の `VITE_*` から来る。欠けていたら黙って別の場所へ
 * 繋ぎに行かず、設定が足りない旨を画面に出して止める。
 */

import {
  AdminSession,
  createAdminClient,
  readAdminAuthConfig,
  readUseMockApi,
} from "visumo-data-agent-web-shared";
import type { Client } from "urql";

export type Bootstrap =
  | { ok: true; session: AdminSession; client: Client; mocked: boolean }
  | { ok: false; missing: string[] };

export async function bootstrap(): Promise<Bootstrap> {
  const env = import.meta.env as unknown as Record<
    string,
    string | boolean | undefined
  >;
  // モックは開発ビルドでしか読み込まない。`import.meta.env.DEV` はビルド時に
  // 真偽値そのものへ置き換わるので、本番ビルドではこの if ごと畳まれて
  // 動的 import が成果物から落ちる
  // （落ちていることは mocks/bundle.test.ts が実際にビルドして確かめる）。
  let mocks: typeof import("./mocks/index") | null = null;
  if (import.meta.env.DEV && readUseMockApi(env, true)) {
    mocks = await import("./mocks/index");
  }

  const configResult = readAdminAuthConfig(
    mocks === null ? env : { ...env, ...mocks.MOCK_ENV },
    { defaultScope: "admin", defaultClientName: "visumo 管理画面(顧客向け)" },
  );
  if (!configResult.ok) {
    return { ok: false, missing: configResult.missing };
  }

  const mockAuth = mocks?.createMockAuthEnvironment() ?? null;
  const session = new AdminSession({
    config: configResult.config,
    ...(mockAuth === null
      ? {}
      : { fetchImpl: mockAuth.fetchImpl, navigate: mockAuth.navigate }),
  });
  const client = createAdminClient({
    session,
    ...(mocks === null ? {} : { mockExchange: mocks.createMockExchange() }),
  });
  return { ok: true, session, client, mocked: mocks !== null };
}
